import React, { useState, useEffect } from 'react'

export const EXPENSE_CATEGORIES = [
  { value: 'housing',       label: 'Housing',       icon: '🏠', color: '#3d4a5c' },
  { value: 'transport',     label: 'Transport',     icon: '🚗', color: '#7a5d1e' },
  { value: 'utilities',     label: 'Utilities',     icon: '⚡', color: '#4a6741' },
  { value: 'education',     label: 'Education',     icon: '📚', color: '#b8441a' },
  { value: 'food',          label: 'Food',          icon: '🍛', color: '#c9a84c' },
  { value: 'healthcare',    label: 'Healthcare',    icon: '❤️', color: '#c0392b' },
  { value: 'entertainment', label: 'Entertainment', icon: '🎬', color: '#6b4c9a' },
  { value: 'other',         label: 'Other',         icon: '📦', color: '#636e72' },
]

const FREQUENCIES = [
  { value: 'monthly',     label: 'Monthly' },
  { value: 'quarterly',   label: 'Quarterly' },
  { value: 'half-yearly', label: 'Half-Yearly' },
  { value: 'yearly',      label: 'Yearly' },
]

function ord(n) {
  n = parseInt(n)
  const s = (n === 1 || n === 21 || n === 31) ? 'st' : (n === 2 || n === 22) ? 'nd' : (n === 3 || n === 23) ? 'rd' : 'th'
  return n + s
}

const inputStyle = {
  background: 'var(--input-bg)', border: '1px solid var(--border)',
  borderRadius: '4px', padding: '0.55rem 0.75rem',
  fontSize: '0.88rem', fontFamily: 'DM Sans, sans-serif',
  color: 'var(--text)', width: '100%', boxSizing: 'border-box',
  outline: 'none', transition: 'border-color 0.18s'
}

const labelStyle = {
  display: 'block', fontFamily: 'monospace',
  fontSize: '0.67rem', fontWeight: '500',
  textTransform: 'uppercase', letterSpacing: '0.08em',
  color: 'var(--text-muted)', marginBottom: '0.35rem'
}

export default function AddExpenseModal({ expense, onSave, onClose }) {
  const [form, setForm] = useState({
    category: 'housing', name: '', amount: '',
    frequency: 'monthly', due_day: '', notes: ''
  })
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (expense) {
      setForm({
        category:  expense.category  || 'housing',
        name:      expense.name      || '',
        amount:    expense.amount    || '',
        frequency: expense.frequency || 'monthly',
        due_day:   expense.due_day   || '',
        notes:     expense.notes     || ''
      })
    }
  }, [expense])

  function set(field, value) { setForm(prev => ({ ...prev, [field]: value })) }

  async function handleSave() {
    if (!form.name.trim()) { setError('Please enter an expense name'); return }
    if (!form.amount || isNaN(form.amount) || parseFloat(form.amount) <= 0) {
      setError('Please enter a valid amount'); return
    }
    setSaving(true)
    setError('')
    await onSave({
      category:  form.category,
      name:      form.name.trim(),
      amount:    parseFloat(form.amount),
      frequency: form.frequency,
      due_day:   form.due_day ? parseInt(form.due_day) : null,
      notes:     form.notes.trim() || null,
    })
    setSaving(false)
  }

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, background: 'var(--modal-backdrop)',
        backdropFilter: 'blur(4px)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
      }}
    >
      <div style={{
        background: 'var(--surface)', borderRadius: '10px',
        border: '1px solid var(--border)', width: '100%', maxWidth: '480px',
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)'
        }}>
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.15rem', fontWeight: '700' }}>
            {expense ? 'Edit Expense' : 'Add Expense'}
          </span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1, padding: '0.2rem' }}>✕</button>
        </div>

        <div style={{ padding: '1.5rem' }}>
          {error && (
            <div style={{ background: 'rgba(184,68,26,0.08)', border: '1px solid rgba(184,68,26,0.2)', borderRadius: '6px', padding: '0.7rem 1rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#b8441a' }}>
              ⚠️ {error}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
            <div>
              <label style={labelStyle}>Category</label>
              <select style={inputStyle} value={form.category} onChange={e => set('category', e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--text)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}>
                {EXPENSE_CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.icon} {c.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Frequency</label>
              <select style={inputStyle} value={form.frequency} onChange={e => set('frequency', e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--text)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}>
                {FREQUENCIES.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>

            <div style={{ gridColumn: '1/-1' }}>
              <label style={labelStyle}>Expense Name</label>
              <input style={inputStyle} placeholder="e.g. House Rent, Petrol, School Fees"
                value={form.name} onChange={e => set('name', e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--text)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </div>

            <div>
              <label style={labelStyle}>Amount (₹)</label>
              <input style={inputStyle} type="text" inputMode="decimal" placeholder="5000"
                value={form.amount} onChange={e => set('amount', e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--text)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </div>

            <div>
              <label style={labelStyle}>Due Day (optional)</label>
              <select style={inputStyle} value={form.due_day} onChange={e => set('due_day', e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--text)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}>
                <option value="">-- Select Day --</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>{ord(d)} of every month</option>
                ))}
              </select>
            </div>

            <div style={{ gridColumn: '1/-1' }}>
              <label style={labelStyle}>Notes (optional)</label>
              <textarea
                style={{ ...inputStyle, resize: 'vertical', minHeight: '64px' }}
                placeholder="Any additional notes..."
                value={form.notes} onChange={e => set('notes', e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--text)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </div>
          </div>
        </div>

        <div style={{ padding: '0.9rem 1.5rem 1.3rem', display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', borderTop: '1px solid var(--border)' }}>
          <button onClick={onClose} style={{ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', padding: '0.5rem 1rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ background: 'var(--btn-bg)', color: 'var(--btn-text)', border: 'none', padding: '0.5rem 1.2rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: '500', cursor: saving ? 'not-allowed' : 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving...' : expense ? 'Update Expense' : 'Add Expense'}
          </button>
        </div>
      </div>
    </div>
  )
}
